/**
 * Linear/Superhuman-style Escape hierarchy. Each open context (palette, modal,
 * picker, side panel, search filter, list selection) registers a handler. A
 * single Escape press pops exactly the topmost handler — never skips levels.
 *
 * Priority order (high → low):
 *  1. text-edit cancel        (priority 50)
 *  2. picker / popover        (priority 40)
 *  3. modal / palette         (priority 30)
 *  4. side panel              (priority 20)
 *  5. clear search filter     (priority 10)
 *  6. clear list selection    (priority 0)
 *
 * Higher numeric priority wins. Ties break by insertion order — the most
 * recently registered handler runs first, which matches how nested portals
 * render in React (children mount last).
 */
type EscapeHandler = {
  id: number;
  priority: number;
  handler: () => boolean | void;
};

let nextId = 1;
const handlers: EscapeHandler[] = [];
let listenerAttached = false;

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== "Escape") return;
  if (handlers.length === 0) return;

  // Sort by priority (desc), then by insertion order (desc — newest first).
  const sorted = [...handlers].sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return b.id - a.id;
  });

  for (const h of sorted) {
    const result = h.handler();
    // Returning `false` lets the next handler try; otherwise we consume Esc.
    if (result === false) continue;
    e.preventDefault();
    e.stopPropagation();
    return;
  }
}

function ensureListener() {
  if (listenerAttached || typeof window === "undefined") return;
  window.addEventListener("keydown", onKeyDown);
  listenerAttached = true;
}

function teardownListener() {
  if (!listenerAttached || typeof window === "undefined") return;
  if (handlers.length > 0) return;
  window.removeEventListener("keydown", onKeyDown);
  listenerAttached = false;
}

/**
 * Register an Escape handler. Pass an idempotent close function that returns
 * `false` if it had nothing to close (the next handler will be tried).
 */
export function registerEscapeHandler(
  handler: () => boolean | void,
  priority: number,
): () => void {
  const entry: EscapeHandler = { id: nextId++, priority, handler };
  handlers.push(entry);
  ensureListener();
  return () => {
    const idx = handlers.findIndex((h) => h.id === entry.id);
    if (idx >= 0) handlers.splice(idx, 1);
    teardownListener();
  };
}

/** Standard priority bands matching the Escape hierarchy. */
export const ESCAPE_PRIORITY = {
  textEdit: 50,
  picker: 40,
  modal: 30,
  sidePanel: 20,
  clearSearch: 10,
  clearSelection: 0,
} as const;

/** Test-only — clears any registered handlers. */
export function _resetEscapeStackForTests() {
  handlers.length = 0;
  if (listenerAttached && typeof window !== "undefined") {
    window.removeEventListener("keydown", onKeyDown);
    listenerAttached = false;
  }
}
